import { PaginatedInfo } from "@/lib/api/interfaces/utils";

import {
  CustomerUserListPaginatedResponseDto,
  CustomerUserListPaginatedResponseDtoWithInvitationCount,
  CustomerUserListResponseDto,
} from "@/lib/api/interfaces/users.interface";
import {
  useUsersCustomerarchiveOne,
  useUsersCustomerFind,
  useUsersCustomerFindInitial,
  UseUsersCustomerFindInitialParams,
  UseUsersCustomerFindParams,
  useUsersCustomerunArchiveOne,
} from "@/lib/api/requests/users.requests";
import { formatDate } from "@/lib/utils/date-formatter";
import { Badge } from "@/ui-components/ui/badge";
import { Button } from "@/ui-components/ui/button";
import ConfirmationDialog from "@/ui-components/ui/confirmation-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/util-components/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui-components/ui/select";
import { SuspenseWrapper } from "@/ui-components/ui/suspense-wrapper";
import {
  DataTable2,
  DataTable2ColumnHeader,
  DataTable2Pagination,
  DataTable2Toolbar,
  getDataTable2SelectionColumn,
  useDataTable2,
} from "@/util-components/data-table-2/components";
import { UseMutateFunction } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "@/lib/toast";
import CreateCustomerInvitationDialog from "./create-dialog";
import { SubPageLayout } from "@/util-components/page-layout/sub-page-layout";
import { MoreHorizontal, Plus, Users } from "lucide-react";

const CustomerUsersWrapper = () => {
  return (
    <SuspenseWrapper type="green" text="Lädt Nutzer...">
      <CustomerUsers />
    </SuspenseWrapper>
  );
};

const getColumns = (
  archiveUser: UseMutateFunction<
    any,
    Error,
    {
      id: string;
    },
    unknown
  >,
  unarchiveUser: UseMutateFunction<
    any,
    Error,
    {
      id: string;
    },
    unknown
  >
): ColumnDef<CustomerUserListResponseDto>[] => {
  return [
    getDataTable2SelectionColumn<CustomerUserListResponseDto>(),
    {
      id: "name",
      accessorFn: (row) => `${row.firstName} ${row.lastName}`,
      meta: { label: "Name" },
      header: ({ column }) => <DataTable2ColumnHeader column={column} title="Name" />,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <div>
            <p>
              {row.original.firstName} {row.original.lastName}
            </p>
            <p className="text-sm text-gray-500">{row.original.mail}</p>
          </div>
          {row.original.ownUser ? <Badge>Du</Badge> : null}
        </div>
      ),
    },
    {
      meta: { label: "Letzter Login" },
      header: ({ column }) => <DataTable2ColumnHeader column={column} title="Letzter Login" />,
      accessorKey: "lastLogin",
      cell: ({ row }) => formatDate(row.original.lastLogin),
      sortingFn: (a, b, id) => {
        const aValue = a.getValue(id);
        const bValue = b.getValue(id);
        const aTime = aValue ? new Date(aValue as any).getTime() : 0;
        const bTime = bValue ? new Date(bValue as any).getTime() : 0;
        return aTime - bTime;
      },
    },
    {
      meta: { label: "Status" },
      header: ({ column }) => <DataTable2ColumnHeader column={column} title="Status" />,
      accessorKey: "isArchive",
      cell: ({ row }) => (row.original.isArchive ? <Badge>Archiviert</Badge> : <Badge>Aktiv</Badge>),
    },
    {
      meta: { label: "Rolle" },
      header: ({ column }) => <DataTable2ColumnHeader column={column} title="Rolle" />,
      accessorKey: "customerRole",
      cell: ({ row }) => <Badge>{row.original.customerRole}</Badge>,
    },
    {
      header: "",
      accessorKey: "actions",
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => {
        const handleArchive = () => {
          if (row.original.isArchive) {
            unarchiveUser({ id: row.original._id });
          } else {
            archiveUser({ id: row.original._id });
          }
        };

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-xl" onClick={(e) => e.stopPropagation()}>
                <MoreHorizontal className="h-4 w-4 text-gray-600" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <ConfirmationDialog
                title={row.original.isArchive ? "Nutzer wiederherstellen" : "Nutzer archivieren"}
                onConfirm={handleArchive}
                description={
                  row.original.isArchive
                    ? "Möchtest du den Nutzer wiederherstellen?"
                    : "Möchtest du den Nutzer archivieren?"
                }
                disabled={!row.original.isArchiveable}
              >
                <DropdownMenuItem onSelect={(e) => e.preventDefault()} disabled={!row.original.isArchiveable}>
                  {row.original.isArchive ? "Wiederherstellen" : "Archivieren"}
                </DropdownMenuItem>
              </ConfirmationDialog>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];
};

const CustomerUsers = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isInitial, setIsInitial] = useState(true);
  const [shouldReload, setShouldReload] = useState<{ shouldReload: boolean; refetch: boolean }>({
    shouldReload: false,
    refetch: false,
  });
  const [users, setUsers] = useState<CustomerUserListResponseDto[]>();
  const [paginatedInfo, setPaginatedInfo] = useState<PaginatedInfo>();
  const [invitationCount, setInvitationCount] = useState<number>(0);
  const initialUiTextSearch = searchParams.get("textSearch") ?? "";
  const [uiTextSearch, setUiTextSearch] = useState<string>(initialUiTextSearch);
  const initialPage = Number.parseInt(searchParams.get("page") ?? "1", 10);
  const [params, setParams] = useState<UseUsersCustomerFindParams>({
    limit: 10,
    page: Number.isFinite(initialPage) && initialPage > 0 ? initialPage : 1,
    ...(initialUiTextSearch.trim().length >= 4 ? { textSearch: initialUiTextSearch.trim() } : {}),
  });
  const [createInvitationDialogOpen, setCreateInvitationDialogOpen] = useState(false);
  const { mutate: archiveUser } = useUsersCustomerarchiveOne();
  const { mutate: unarchiveUser } = useUsersCustomerunArchiveOne();

  const onDataLoadedInitial = (data: CustomerUserListPaginatedResponseDtoWithInvitationCount) => {
    if (Array.isArray(data?.users?.docs)) {
      setUsers(data.users.docs);
      setPaginatedInfo(data.users);
      setInvitationCount(data.invitationCount);
      setIsInitial(false);
      return;
    }
    setUsers([]);
    setPaginatedInfo(undefined);
    setInvitationCount(0);
    setIsInitial(false);
  };

  const onDataLoaded = (data: CustomerUserListPaginatedResponseDto) => {
    if (Array.isArray(data?.docs)) {
      setUsers(data.docs);
      setPaginatedInfo(data);
    }
    setShouldReload({ shouldReload: false, refetch: false });
  };

  useEffect(() => {
    if (!isInitial) {
      setShouldReload({ shouldReload: true, refetch: false });
    }
    setSearchParams(
      {
        page: (params.page ?? 1).toString(),
        ...(params.textSearch ? { textSearch: params.textSearch } : {}),
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const navigate = useNavigate();

  const columns = getColumns(
    (id) => {
      archiveUser(id, {
        onSuccess: () => {
          toast.success("Nutzer erfolgreich archiviert");
          setShouldReload({ shouldReload: true, refetch: true });
        },
        onError: (error) => {
          toast.error("Nutzer konnte nicht archiviert werden", { error });
        },
      });
    },
    (id) => {
      unarchiveUser(id, {
        onSuccess: () => {
          toast.success("Nutzer erfolgreich wiederhergestellt");
          setShouldReload({ shouldReload: true, refetch: true });
        },
        onError: (error) => {
          toast.error("Nutzer konnte nicht wiederhergestellt werden", { error });
        },
      });
    }
  );

  const { table } = useDataTable2({
    data: users || [],
    columns,
    enableRowSelection: true,
    getRowId: (row) => row._id,
  });

  const selectedCount = table.getSelectedRowModel().rows.length;
  const rowCount = table.getRowModel().rows.length;

  return (
    <SubPageLayout
      pageTitle="Mitglieder & Rollen"
      parentPageTitle="Dein Verein"
      parentPageCallback={() => navigate("/vereine")}
      navigateBackCallback={() => navigate("/vereine")}
      icon={<Users className="h-6 w-6" />}
    >
        <div className="max-w-7xl mx-auto space-y-6">
        <SuspenseWrapper
          type="green"
          text="Lädt Nutzer..."
        >
          {isInitial && <CustomerUsersInitial onDataLoaded={onDataLoadedInitial} params={params} />}
        </SuspenseWrapper>
        <SuspenseWrapper
          type="green"
          text="Lädt Nutzer..."
        >
          {!isInitial && shouldReload.shouldReload && (
            <CustomerUsersFind onDataLoaded={onDataLoaded} params={params} shouldRefetch={shouldReload.refetch} />
          )}
        </SuspenseWrapper>

        <div className="bg-white overflow-hidden">
          <DataTable2Toolbar
            className="px-6"
            table={table}
            searchQuery={uiTextSearch}
            onSearchChange={(text) => {
              setUiTextSearch(text);
              const normalized = text.trim();
              if (normalized.length === 0) {
                setParams({ ...params, textSearch: undefined, page: 1 });
                return;
              }
              if (normalized.length >= 4) {
                setParams({ ...params, textSearch: normalized, page: 1 });
              }
            }}
            searchHint={uiTextSearch.trim().length > 0 && uiTextSearch.trim().length < 4 ? "Mindestens 4 Zeichen" : undefined}
            columnTogglePosition="left"
            left={
              <Select defaultValue="all">
                <SelectTrigger className="h-10 w-[180px] rounded-xl bg-white border-gray-200">
                  <SelectValue placeholder="Auswahl" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Mitglieder</SelectItem>
                </SelectContent>
              </Select>
            }
            right={
              <div className="flex flex-wrap items-center justify-end gap-2">
                {selectedCount > 0 ? (
                  <ConfirmationDialog
                    title="Nutzer archivieren"
                    description={`Möchtest du ${selectedCount} ausgewählte Nutzer archivieren?`}
                    confirmText="Archivieren"
                    onConfirm={() => {
                      const selected = table.getSelectedRowModel().rows.map((r) => r.original);
                      const ids = selected
                        .filter((u) => u.isArchiveable)
                        .filter((u) => !u.isArchive)
                        .map((u) => u._id);

                      ids.forEach((id) => archiveUser({ id }));
                      if (ids.length > 0) {
                        toast.success("Archivierung gestartet");
                        setShouldReload({ shouldReload: true, refetch: true });
                        table.resetRowSelection();
                      } else {
                        toast.error("Keine auswählbaren Nutzer zum Archivieren");
                      }
                    }}
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-10 rounded-xl bg-white border-gray-200 hover:bg-gray-50"
                    >
                      Archivieren ({selectedCount})
                    </Button>
                  </ConfirmationDialog>
                ) : null}

                <Link to="/users/invitations">
                  <Button variant="outline" size="sm" className="h-10 rounded-xl bg-white border-gray-200 hover:bg-gray-50">
                    Einladungen ({invitationCount || 0})
                  </Button>
                </Link>

                <Button
                  size="sm"
                  className="h-10 rounded-xl"
                  onClick={() => setCreateInvitationDialogOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nutzer einladen
                </Button>
              </div>
            }
          />

          <div className="px-6 pb-4 pt-4">
            <DataTable2 table={table} />
          </div>

          <div className="border-t border-gray-100 px-6 py-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="text-sm text-gray-500">{selectedCount > 0 ? `${selectedCount} von ${rowCount} ausgewählt.` : null}</div>
              {paginatedInfo ? (
                <DataTable2Pagination
                  currentPage={params.page ?? 1}
                  pageCount={paginatedInfo.pageCount ?? 0}
                  hasNextPage={paginatedInfo.hasNextPage ?? false}
                  hasPrevPage={paginatedInfo.hasPrevPage ?? false}
                  onPageChange={(page) => setParams({ ...params, page })}
                  pageSize={params.limit ?? 10}
                  onPageSizeChange={(limit) => setParams({ ...params, limit, page: 1 })}
                />
              ) : null}
            </div>
          </div>
        </div>

        <SuspenseWrapper type="green" text="Öffnet Dialog...">
          {createInvitationDialogOpen && (
            <CreateCustomerInvitationDialog
              open={createInvitationDialogOpen}
              onClose={(refetchData) => {
                setCreateInvitationDialogOpen(false);
                if (refetchData) {
                  setShouldReload({ shouldReload: true, refetch: true });
                }
              }}
            />
          )}
        </SuspenseWrapper>
      </div>
    </SubPageLayout>
  );
};

const CustomerUsersInitial = ({
  onDataLoaded,
  params,
}: {
  onDataLoaded: (data: CustomerUserListPaginatedResponseDtoWithInvitationCount) => void;
  params: UseUsersCustomerFindInitialParams;
}) => {
  const { data: response } = useUsersCustomerFindInitial(params);

  useEffect(() => {
    if (response) {
      onDataLoaded(response);
    }
  }, [response, onDataLoaded]);

  return null;
};

const CustomerUsersFind = ({
  onDataLoaded,
  params,
  shouldRefetch,
}: {
  onDataLoaded: (data: CustomerUserListPaginatedResponseDto) => void;
  params: UseUsersCustomerFindParams;
  shouldRefetch: boolean;
}) => {
  const { data: response, refetch } = useUsersCustomerFind(params);

  const refetchData = useCallback(async () => {
    const { data } = await refetch();
    if (data) {
      onDataLoaded(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch]);

  useEffect(() => {
    if (response) {
      onDataLoaded(response);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response, onDataLoaded]);

  useEffect(() => {
    if (shouldRefetch) {
      refetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRefetch]);

  return null;
};

export default CustomerUsersWrapper;
